'use client';

import { useEffect, useState, memo } from 'react';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { buildRoute } from '@headless-workspace/config';
import { UIHoverBanner, UIHoverBannerButton } from '@headless-workspace/core-ui';
import {
    GlowBasketProductPlot,
    GlowButton,
    GlowChip,
    GlowClickable,
    GlowDivider,
    GlowShippingInformation,
    GlowTextBody,
    GlowTextBodySmall,
    Icons,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';
import {
    BasketBusinessRules,
    getConnectedUserInfo,
    isUserGold,
    PriceInformation,
    RichText,
    useNavigation,
    useUserInfo,
} from '../../../../src';
import { useBasketProductsList } from './useBasketProductsList';

type BasketSummaryContainerProps = PropsWithLocale & {
    totalForFreeShipping: number;
    enablePriceExceptionLegalMentions: boolean;
};

const BASKET_INFORMATION_ID = 'basket-information';

export const BasketSummaryContainer: ComponentMemo<BasketSummaryContainerProps> = memo(
    ({ locale, totalForFreeShipping, enablePriceExceptionLegalMentions }) => {
        const t = useTranslations('Header.basket.banner');
        const navigation = useNavigation();
        const pathname = usePathname();
        const { isLoading, result: basketResult } = useBasketProductsList();
        const { result: userInfoResult } = useUserInfo();
        const userInfo = getConnectedUserInfo(userInfoResult);
        const isGoldUser = userInfo && isUserGold(userInfo);
        const [isRedirecting, setIsRedirecting] = useState(false);

        const onMoreBasketClick = () => {
            setIsRedirecting(true);
            navigation.push(buildRoute(locale, 'basket'));
        };

        useEffect(() => {
            setIsRedirecting(false);
        }, [pathname]);

        if (basketResult.type !== 'success' || basketResult.data.productsCount <= 0) {
            // FIXME: This if will become useless once we've found a solution to avoid duplicate icons
            return (
                <UIHoverBannerButton
                    className={'h-full fill-text-primary'}
                    href={buildRoute(locale, 'basket')}
                    ariaLabel={t('ariaLabel.emptyBasket')}
                    Icon={Icons.Basket}
                    isLoading={false}
                />
            );
        }

        const {
            data: { currency, totalBeforeDiscount, products, productsCount, shipments, vatPrice, total },
        } = basketResult;
        const { productsDisplayed, quantityOfProductsNotDisplayed, isFreeShipping, hasOmnibusProduct } =
            BasketBusinessRules({ products, productsCount, totalBeforeDiscount, totalForFreeShipping, userInfo });

        const quantityOfProductsNotDisplayedLabel =
            quantityOfProductsNotDisplayed > 0
                ? t('label.quantityOfProductsNotDisplayed', {
                      quantityOfProductsNotDisplayed,
                  })
                : '';

        const subtotalLabel = t('label.subtotalLabel', {
            subTotalItems: productsCount,
        });

        const totalForShipping = isGoldUser ? totalForFreeShipping : totalBeforeDiscount;

        const shippingCostsLabel = shipments?.shippingOptions
            ? t('label.shippingCost', {
                  checkIsFreeShipping: isFreeShipping,
                  shippingOptions: shipments.shippingOptions,
              })
            : '';

        const shippingCostsWithCurrency = shipments
            ? t('label.shippingCostsWithCurrency', {
                  checkIsFreeShipping: isFreeShipping || shipments?.priceOfShipment === 0,
                  shipmentPrice: StringHelpers.formatPrice({ price: shipments.priceOfShipment, currency, locale }),
              })
            : '';

        const remainderForFreeShippingLabel = t('label.remainderForFreeShippingLabel', {
            checkIsFreeShipping: isFreeShipping,
            amountUntilFreeShipping: StringHelpers.formatPrice({
                price: totalForFreeShipping - (totalForShipping ?? 0),
                currency,
                locale,
            }),
        });

        return (
            <UIHoverBanner
                trailingRight={true}
                className={clsx('flex-row w-basketBanner !p-0')}
                hoverItem={
                    <>
                        <UIHoverBannerButton
                            className={'h-full fill-text-primary'}
                            href={buildRoute(locale, 'basket')}
                            ariaLabel={t('ariaLabel.basket')}
                            Icon={Icons.Basket}
                            isLoading={false}
                        />
                        <GlowClickable
                            href={buildRoute(locale, 'basket')}
                            content={{
                                labelElement: (
                                    <GlowChip
                                        hasDigit={true}
                                        count={productsCount}
                                        className={
                                            'absolute top-0.125 -right-0.125 cursor-pointer group-hover:decoration-text-disabled'
                                        }
                                    />
                                ),
                            }}
                        />
                    </>
                }
            >
                {!isLoading && (
                    <div className={'flex flex-col w-full max-h-basketSummary'}>
                        <GlowTextBody
                            fontWeight={'bold'}
                            text={t('label.title', {
                                numberOfItems: basketResult.data?.productsCount ?? 0,
                            })}
                            className={'line-clamp-1 p-1 flex-shrink-0'}
                            TagName={'h4'}
                        />

                        <GlowDivider />
                        <div className={'pt-1 overflow-y-scroll'}>
                            {productsDisplayed.map((product) => (
                                <GlowBasketProductPlot
                                    key={product.id}
                                    {...product}
                                    price={StringHelpers.formatPriceWithConditions(
                                        product.price,
                                        currency,
                                        locale,
                                        false,
                                        product.quantity,
                                    )}
                                    priceBeforeDiscount={StringHelpers.formatPrice({
                                        price: product.priceBeforeDiscount,
                                        currency,
                                        locale,
                                    })}
                                    textQuantityLabel={t('label.quantity', {
                                        quantity: product.quantity,
                                    })}
                                    discountPercentageLabel={
                                        product.discountPercentage
                                            ? t('label.discountPercentage', {
                                                  discountPercentage: product.discountPercentage,
                                              })
                                            : undefined
                                    }
                                    ImageTagName={Image}
                                    classNames={{
                                        container: 'px-1',
                                        mainContent: 'flex-col',
                                        extraContent: 'flex-col',
                                    }}
                                    fullWidth
                                />
                            ))}
                        </div>

                        <div className={'flex flex-col gap-1 px-1 py-1'}>
                            <div id={BASKET_INFORMATION_ID}>
                                {quantityOfProductsNotDisplayed > 0 && (
                                    <GlowTextBodySmall text={quantityOfProductsNotDisplayedLabel} className={'pb-1'} />
                                )}
                                <PriceInformation
                                    className={'pb-0.5'}
                                    label={subtotalLabel}
                                    price={StringHelpers.formatPrice({ price: totalBeforeDiscount, currency, locale })}
                                    fontWeight={'bold'}
                                />
                                <PriceInformation
                                    className={'pb-0.5 bold'}
                                    label={shippingCostsLabel}
                                    price={shippingCostsWithCurrency}
                                />
                                <PriceInformation
                                    className={'pb-0.5 bold'}
                                    label={t('label.total')}
                                    price={StringHelpers.formatPrice({ price: total, currency, locale })}
                                    shouldDisplayVat={true}
                                    vatPrice={StringHelpers.formatPrice({
                                        price: vatPrice,
                                        currency,
                                        locale,
                                        defaultValue: null,
                                    })}
                                    fontWeight={'bold'}
                                />
                            </div>
                            <GlowShippingInformation
                                totalBasket={totalForShipping}
                                totalForFreeShipping={totalForFreeShipping}
                                remainderForFreeShippingLabel={remainderForFreeShippingLabel}
                                currency={currency}
                            />
                            <GlowButton
                                className={'mt-1 w-full transition-backgroundOutline'}
                                label={t('action.seeBasket')}
                                onClick={onMoreBasketClick}
                                disabled={isRedirecting}
                                isSpinnerActive={isRedirecting}
                            />
                            {hasOmnibusProduct && (
                                <RichText
                                    TagName={'span'}
                                    fontSize={'caption'}
                                    className={'line-clamp-2'}
                                    messageKey={
                                        enablePriceExceptionLegalMentions
                                            ? 'Header.basket.banner.legalmentions.originalPriceException'
                                            : 'Header.basket.banner.legalmentions.originalPrice'
                                    }
                                    color={'tertiary'}
                                />
                            )}
                        </div>
                    </div>
                )}
            </UIHoverBanner>
        );
    },
);

BasketSummaryContainer.displayName = 'BasketSummaryContainer';
